import { CommonModule } from '@angular/common';
import { Component, computed, inject, signal } from '@angular/core';
import { ButtonComponent, InputComponent, SkeletonComponent } from 'nebula-ui-kit';
import { catchError, finalize, of } from 'rxjs';
import { ProfileService } from '../../../../data-access/src/lib/profile/profile.service';
import { ServerContextService } from '../servers/server-context.service';
import {
  FieldLabelComponent,
  GridFormComponent,
  GridFormItemComponent,
  applyVaultModuleInitialLoadErrorSignals,
  toModuleAccessErrorMessage,
  VaultModuleShellComponent,
  VaultPageAlertComponent,
} from '@nebula-shared/ui';
import {
  AuthService,
  VaultAuthService,
  VaultSessionService,
  deriveSimappeLoginUsername,
  normalizeVaultRoleToken,
  readVaultTenantIdFromAssignmentRow,
  readActiveTenantIdFromAuthMeRecord,
  syncVaultOperativeRoleFromTenantAssignments,
  persistVaultUserSwitchedTenant,
  readVaultIsSuperadminScope,
  readVaultUserSwitchedTenant,
  readVaultOperativeRole,
  vaultOperativeRoleLabel,
  vaultUserIsAdminEmpresaFromSession,
  validateSimappeProvisionPassword,
} from '@nebula/shared-vault/data-access';
import { persistActiveTenantId } from '../../../../data-access/src/lib/shared/tenant-context.util';
import { VaultTenantScopeService } from '../../../../data-access/src/lib/shared/vault-tenant-scope.service';
import type { UserMeResponse } from '../../../../domain/src/lib/user-profile/user-profile.model';
import type { AdminSystemUser } from '../../../../domain/src/lib/admin-users/admin-user.model';
import {
  doesAdminSimappePasswordBlockSave,
  resolveAdminUserFormApiError,
  tenantRoleLabel,
} from '../admin-users/admin-users.utils';
import { AdminUsersInputAutocompleteDirective } from '../admin-users/admin-users-input-autocomplete.directive';
import { AdminUserSecretFieldComponent } from '../admin-users/admin-user-secret-field.component';
import { AdminUserPasswordRulesComponent } from '../admin-users/admin-user-password-rules.component';

@Component({
  selector: 'app-user-profile',
  standalone: true,
  imports: [
    CommonModule,
    GridFormComponent,
    GridFormItemComponent,
    FieldLabelComponent,
    InputComponent,
    ButtonComponent,
    SkeletonComponent,
    VaultModuleShellComponent,
    AdminUsersInputAutocompleteDirective,
    AdminUserSecretFieldComponent,
    AdminUserPasswordRulesComponent,
    VaultPageAlertComponent,
  ],
  templateUrl: './user-profile.component.html',
  styleUrls: ['./user-profile.component.scss'],
})
export class UserProfileComponent {
  private static readonly MODULE_LABEL = 'Perfil';
  private static readonly DISPLAY_NAME_STORAGE_PREFIX = 'vault_profile_display_name_v1:';

  private readonly profileService = inject(ProfileService);
  private readonly serverContext = inject(ServerContextService);
  private readonly vaultAuth = inject(VaultAuthService);
  private readonly auth = inject(AuthService);
  private readonly vaultSession = inject(VaultSessionService);
  private readonly vaultTenantScope = inject(VaultTenantScopeService);

  readonly accessDenied = signal(false);
  readonly loadError = signal<string | null>(null);
  readonly loadErrorTitle = signal('');
  readonly loading = signal(true);
  readonly saving = signal(false);
  readonly error = signal<string | null>(null);
  readonly info = signal<string | null>(null);

  readonly userId = signal('');
  readonly email = signal('');
  readonly fullName = signal('');
  readonly savedFullName = signal('');
  readonly isActive = signal(true);
  readonly companyLabel = signal('—');
  readonly roleLabel = signal('—');
  readonly isSuperadminProfile = signal(false);
  readonly isAdminEmpresaProfile = signal(false);

  readonly formPassword = signal('');
  readonly currentPassword = signal('');

  readonly formAutofillNonce = signal('');
  readonly secretFieldResetToken = signal(0);
  readonly showFormPassword = signal(false);
  readonly showCurrentPassword = signal(false);

  readonly loginUsername = computed(() => deriveSimappeLoginUsername(this.email()));

  readonly canUseAdminPasswordReset = computed(
    () => this.isSuperadminProfile() || this.isAdminEmpresaProfile(),
  );

  readonly formIntroText = computed(() =>
    'Actualizá tu nombre visible y, si lo necesitás, tu clave de acceso. Empresa y rol solo los gestiona un administrador.',
  );
  readonly fullNameFieldTooltip = computed(
    () => 'Nombre visible en listados y reportes. Si lo deja vacío, se conserva el actual.',
  );
  readonly emailFieldTooltip = computed(() => 'El correo no se puede cambiar desde el perfil.');
  readonly loginAccessFieldTooltip = computed(
    () => 'Usuario con el que ingresa al sistema (parte antes de @).',
  );
  readonly companyFieldTooltip = computed(
    () => 'Empresa asignada por un administrador. No editable desde el perfil.',
  );
  readonly roleFieldTooltip = computed(
    () => 'Rol operativo en Vault. No editable desde el perfil.',
  );
  readonly adminPasswordFieldTooltip = computed(
    () => 'Opcional. Deje vacía para conservar la clave actual.',
  );
  readonly selfPasswordFieldTooltip = computed(
    () => 'Debe ingresar su contraseña actual para confirmar el cambio.',
  );

  readonly formPasswordBlocksSave = computed(() =>
    doesAdminSimappePasswordBlockSave(this.formPassword().trim().length),
  );

  readonly profileSaveDisabled = computed(
    () => this.saving() || this.formPasswordBlocksSave(),
  );

  constructor() {
    this.refreshFormAutofillNonce();
    this.auth.syncVaultTenantFromAccessToken();
    this.vaultTenantScope.ensureHydrated$().subscribe();

    this.profileService
      .loadProfile()
      .pipe(
        catchError((err: unknown) => {
          if (this.readLocalUser()) {
            this.info.set(
              'Mostrando datos guardados en sesión; no se pudo sincronizar con el servidor.',
            );
            return of(null);
          }
          applyVaultModuleInitialLoadErrorSignals(
            err,
            {
              moduleLabel: UserProfileComponent.MODULE_LABEL,
              fallback: 'No se pudo cargar el perfil desde el servidor.',
            },
            {
              accessDenied: this.accessDenied,
              loadError: this.loadError,
              loadErrorTitle: this.loadErrorTitle,
            },
          );
          return of(null);
        }),
        finalize(() => this.loading.set(false)),
      )
      .subscribe({
        next: (me) => {
          if (me) this.applyProfileMe(me);
        },
      });
  }

  toggleFormPasswordVisibility(): void {
    this.showFormPassword.update((visible) => !visible);
  }

  toggleCurrentPasswordVisibility(): void {
    this.showCurrentPassword.update((visible) => !visible);
  }

  saveChanges(): void {
    const userId = this.userId().trim();
    const email = this.email().trim();
    const editedName = this.fullName().trim();
    const name = editedName || this.savedFullName().trim() || this.loginUsername().trim();
    const adminReset = this.canUseAdminPasswordReset();
    const newPassword = this.formPassword().trim();
    const currentPassword = this.currentPassword().trim();
    const wantsPasswordChange = adminReset
      ? !!newPassword
      : !!(currentPassword || newPassword);
    const nameChanged = !!editedName && editedName !== this.savedFullName().trim();

    if (!userId) {
      this.error.set('No se pudo identificar el usuario. Recargue la sesión e intente de nuevo.');
      return;
    }
    if (!name) {
      this.error.set('No se pudo determinar el nombre para mostrar. Recargue la sesión e intente de nuevo.');
      return;
    }
    if (!email) {
      this.error.set('No se pudo determinar el correo del usuario.');
      return;
    }
    if (!nameChanged && !wantsPasswordChange) {
      this.info.set('No hay cambios para guardar.');
      return;
    }

    if (!adminReset && wantsPasswordChange) {
      if (!currentPassword || !newPassword) {
        this.error.set('Para cambiar la clave completá contraseña actual y nueva.');
        return;
      }
      const passwordError = validateSimappeProvisionPassword(newPassword);
      if (passwordError) {
        this.error.set(passwordError);
        return;
      }
      if (currentPassword === newPassword) {
        this.error.set('La nueva contraseña debe ser diferente a la actual.');
        return;
      }
    }

    if (adminReset && newPassword) {
      const passwordError = validateSimappeProvisionPassword(newPassword);
      if (passwordError) {
        this.error.set(passwordError);
        return;
      }
    }

    this.saving.set(true);
    this.error.set(null);
    this.info.set(null);

    this.profileService
      .saveProfileChanges({
        userId,
        data: {
          full_name: name,
          email,
          is_active: this.isActive(),
          ...(wantsPasswordChange ? { password: newPassword } : {}),
        },
        adminPasswordReset: adminReset,
        currentPassword,
        nameChanged,
        wantsPasswordChange,
      })
      .pipe(finalize(() => this.saving.set(false)))
      .subscribe({
        next: (saved) => {
          const savedName = this.readSavedFullName(saved) || name;
          if (nameChanged) {
            this.persistDisplayNameLocal(userId, savedName);
            this.applyDisplayName(savedName);
          }
          if (wantsPasswordChange) {
            this.clearPasswordFields();
          }
          this.info.set(this.buildSuccessMessage(nameChanged, wantsPasswordChange));
        },
        error: (err: unknown) => {
          const resolved = resolveAdminUserFormApiError(err);
          this.error.set(
            resolved ??
              toModuleAccessErrorMessage(err, {
                moduleLabel: 'Perfil',
                fallback: wantsPasswordChange
                  ? 'No se pudieron guardar el perfil o la contraseña.'
                  : 'No se pudo guardar el perfil.',
              }),
          );
        },
      });
  }

  private buildSuccessMessage(nameChanged: boolean, passwordChanged: boolean): string {
    if (nameChanged && passwordChanged) return 'Perfil y contraseña actualizados.';
    if (passwordChanged) return 'Contraseña actualizada.';
    return 'Nombre para mostrar actualizado.';
  }

  private applyProfileMe(me: UserMeResponse): void {
    const userId = String(me.id ?? '').trim();
    this.userId.set(userId);
    this.email.set(me.email ?? '');
    const displayName = this.resolveDisplayNameFromSources(me, null, userId);
    this.applyDisplayName(displayName);
    this.isActive.set(me.is_active !== false);

    const meRecord = me as unknown as Record<string, unknown>;
    const assignedFromMe = readActiveTenantIdFromAuthMeRecord(meRecord);
    const meTenants = me.tenants ?? [];
    const firstAssigned = meTenants.length
      ? readVaultTenantIdFromAssignmentRow(meTenants[0] as Record<string, unknown>)
      : '';
    const assigned = assignedFromMe || firstAssigned;

    const isSuperadmin =
      Boolean(me.is_superadmin) ||
      this.auth.isVaultSuperadmin() ||
      Boolean(readVaultIsSuperadminScope());
    this.isSuperadminProfile.set(isSuperadmin);

    let roleToken = '';
    if (isSuperadmin) {
      this.companyLabel.set('—');
      this.roleLabel.set('Superadmin');
      this.isAdminEmpresaProfile.set(false);
    } else {
      const activeRow =
        meTenants.find((t) => {
          const row = t as Record<string, unknown>;
          const tid = readVaultTenantIdFromAssignmentRow(row);
          return assigned && tid === assigned;
        }) ??
        meTenants[0] ??
        null;
      const row = activeRow as Record<string, unknown> | null;
      const tenantName = String(row?.['tenant_name'] ?? row?.['name'] ?? '').trim();
      roleToken = String(row?.['role'] ?? readVaultOperativeRole() ?? '').trim();
      const sessionCtx = this.vaultSession.getActiveTenantContext();

      this.companyLabel.set(
        tenantName || sessionCtx.companyLabel?.trim() || 'Sin empresa',
      );
      this.roleLabel.set(
        roleToken
          ? tenantRoleLabel(roleToken)
          : vaultOperativeRoleLabel(readVaultOperativeRole() ?? ''),
      );
      this.isAdminEmpresaProfile.set(
        normalizeVaultRoleToken(roleToken) === 'admin_empresa' ||
          vaultUserIsAdminEmpresaFromSession(),
      );
    }

    const stored = this.vaultSession.getTenantId();
    const userSwitched = readVaultUserSwitchedTenant();
    const resolved = userSwitched
      ? stored ||
        assigned ||
        readVaultTenantIdFromAssignmentRow(meTenants[0] as Record<string, unknown>) ||
        ''
      : assigned ||
        stored ||
        readVaultTenantIdFromAssignmentRow(meTenants[0] as Record<string, unknown>) ||
        '';

    if (resolved) {
      persistActiveTenantId(resolved, this.serverContext);
      syncVaultOperativeRoleFromTenantAssignments(resolved);
      persistVaultUserSwitchedTenant(isSuperadmin || (!!assigned && resolved !== assigned));
      if (typeof globalThis.window !== 'undefined') {
        globalThis.window.dispatchEvent(new CustomEvent('vault-session-updated'));
      }
    }
  }

  private clearPasswordFields(): void {
    this.formPassword.set('');
    this.currentPassword.set('');
    this.showFormPassword.set(false);
    this.showCurrentPassword.set(false);
    this.secretFieldResetToken.update((token) => token + 1);
  }

  private refreshFormAutofillNonce(): void {
    this.formAutofillNonce.set(
      typeof crypto !== 'undefined' && 'randomUUID' in crypto
        ? crypto.randomUUID()
        : `nonce-${Date.now()}`,
    );
  }

  private applyDisplayName(name: string): void {
    const normalized = name.trim();
    this.savedFullName.set(normalized);
    this.fullName.set(normalized);
  }

  private readSavedFullName(saved: AdminSystemUser | void): string {
    if (!saved || typeof saved !== 'object') return '';
    return String((saved as AdminSystemUser).full_name ?? '').trim();
  }

  private persistDisplayNameLocal(userId: string, name: string): void {
    if (!userId || !name || typeof globalThis.localStorage === 'undefined') return;
    globalThis.localStorage.setItem(
      `${UserProfileComponent.DISPLAY_NAME_STORAGE_PREFIX}${userId}`,
      name,
    );
    const raw = globalThis.localStorage.getItem('user_data');
    if (!raw) return;
    try {
      const data = JSON.parse(raw) as Record<string, unknown>;
      data['full_name'] = name;
      data['name'] = name;
      globalThis.localStorage.setItem('user_data', JSON.stringify(data));
    } catch {
      /* ignore */
    }
  }

  private readPersistedDisplayName(userId: string): string {
    if (!userId || typeof globalThis.localStorage === 'undefined') return '';
    return (
      globalThis.localStorage
        .getItem(`${UserProfileComponent.DISPLAY_NAME_STORAGE_PREFIX}${userId}`)
        ?.trim() ?? ''
    );
  }

  private resolveNameForSave(): string {
    return (
      this.fullName().trim() ||
      this.savedFullName().trim() ||
      this.loginUsername().trim()
    );
  }

  private resolveDisplayNameFromSources(
    me?: UserMeResponse | null,
    localRec?: Record<string, unknown> | null,
    userId?: string,
  ): string {
    const fromMe = String(me?.full_name ?? me?.name ?? '').trim();
    if (fromMe) return fromMe;

    const fromPersisted = this.readPersistedDisplayName(userId ?? this.userId().trim());
    if (fromPersisted) return fromPersisted;

    const fromLocal = localRec
      ? String(localRec['full_name'] ?? localRec['name'] ?? '').trim()
      : '';
    if (fromLocal) return fromLocal;

    const email = String(me?.email ?? localRec?.['email'] ?? this.email()).trim();
    return deriveSimappeLoginUsername(email);
  }

  private readLocalUser(): boolean {
    const u = this.vaultAuth.getUserData();
    if (!u || typeof u !== 'object') return false;
    const rec = u as Record<string, unknown>;
    const id = rec['id'] ?? rec['user_id'];
    const userId = typeof id === 'string' ? id.trim() : '';
    if (userId) this.userId.set(userId);
    const email = rec['email'];
    if (typeof email === 'string') this.email.set(email);
    const displayName = this.resolveDisplayNameFromSources(null, rec, userId);
    this.applyDisplayName(displayName);
    this.isAdminEmpresaProfile.set(vaultUserIsAdminEmpresaFromSession());
    return true;
  }
}
